import { useState } from 'react';
import { fetchApi } from '../../lib/api';

// Props:
//   eventId         - event ID
//   companyHour     - { rep_name, rep_department, hour_start, hour_end, company_id }
//   subSlots        - array of { id, slot_start, slot_end, status, inventor_id }
//   inventorBookedTimes - Set of ISO time strings this inventor already has booked
//   currentUserId   - the current inventor's user ID for booked-by-me detection
//   onBooked        - callback after successful booking
//   onCancel        - callback to close/cancel
export default function SubSlotPicker({ eventId, companyHour, subSlots, inventorBookedTimes, currentUserId, onBooked, onCancel }) {
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState('');

  const hourStart = new Date(companyHour.hour_start);

  // Generate 4 x 15-min cells from the company hour
  const cells = Array.from({ length: 4 }, (_, i) => {
    const start = new Date(hourStart.getTime() + i * 15 * 60 * 1000);
    const end = new Date(start.getTime() + 15 * 60 * 1000);
    const matchingSlot = subSlots.find(s => new Date(s.slot_start).getTime() === start.getTime());
    const isBookedByMe = matchingSlot?.status === 'booked' && currentUserId && matchingSlot.inventor_id === currentUserId;
    const isBookedByOthers = matchingSlot?.status === 'booked' && !isBookedByMe;
    const isAvailable = matchingSlot?.status === 'available';
    const isDoubleBooked = isAvailable && inventorBookedTimes?.has(start.toISOString());
    const isSelected = selectedSlotId === matchingSlot?.id;
    return { start, end, slot: matchingSlot, isAvailable, isBookedByOthers, isBookedByMe, isDoubleBooked, isSelected };
  });

  const handleBook = async () => {
    if (!selectedSlotId) return;
    setBooking(true);
    setError('');
    try {
      await fetchApi(`/api/events/${eventId}/book-slot`, {
        method: 'POST',
        body: JSON.stringify({ slot_id: selectedSlotId }),
      });
      onBooked?.();
    } catch (err) {
      console.error('Slot booking error:', err);
      setError(err.message || 'Failed to book slot');
    }
    setBooking(false);
  };

  const formatTime = (date) => date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  // Cell style map (per UI-SPEC Sub-Slot Time Grid)
  const getCellStyle = (cell) => {
    const base = {
      minHeight: 44,
      minWidth: 64,
      borderRadius: 8,
      fontSize: 14,
      fontWeight: 600,
      fontFamily: 'Poppins, sans-serif',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.15s ease',
      border: '1px solid transparent',
    };

    if (cell.isBookedByMe) {
      return { ...base, background: 'linear-gradient(135deg, #c9a84c, #F59E0B)', color: '#fff', cursor: 'default' };
    }
    if (cell.isDoubleBooked) {
      return { ...base, background: '#FEF2F2', color: '#F97066', cursor: 'not-allowed' };
    }
    if (cell.isBookedByOthers) {
      return { ...base, background: '#F1F5F9', color: '#CBD5E1', cursor: 'not-allowed' };
    }
    if (cell.isSelected) {
      return { ...base, background: '#EEF2FF', color: '#4F46E5', border: '2px solid #4F46E5', cursor: 'pointer' };
    }
    if (cell.isAvailable) {
      return { ...base, background: '#ECFDF5', color: '#10B981', border: '1px solid #10B981', cursor: 'pointer' };
    }
    // No slot or unknown status
    return { ...base, background: '#F1F5F9', color: '#CBD5E1', cursor: 'not-allowed' };
  };

  const hasDoubleBooked = cells.some(c => c.isDoubleBooked);

  return (
    <div style={{ padding: 16 }}>
      {/* Per-rep header (D-14) */}
      <div style={{ marginBottom: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#0F172A', fontFamily: 'Poppins, sans-serif' }}>
          {companyHour.rep_name || 'Company Rep'}
        </span>
        {companyHour.rep_department && (
          <span style={{ fontSize: 11, color: '#64748B', fontFamily: 'Poppins, sans-serif', marginLeft: 8 }}>
            {companyHour.rep_department}
          </span>
        )}
        <div style={{ fontSize: 11, color: '#94A3B8', fontFamily: 'Poppins, sans-serif', marginTop: 2 }}>
          {formatTime(hourStart)} &mdash; {formatTime(new Date(companyHour.hour_end))}
        </div>
      </div>

      {/* Time grid: 4 cells horizontally (D-15, D-16) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(64px, 1fr))', gap: 8 }}>
        {cells.map((cell, i) => {
          const style = getCellStyle(cell);
          const clickable = cell.isAvailable && !cell.isDoubleBooked && !cell.isBookedByMe;
          const title = cell.isBookedByMe
            ? 'Your booked slot'
            : cell.isDoubleBooked
            ? 'You already have a slot at this time'
            : cell.isBookedByOthers
            ? 'Booked'
            : cell.isAvailable
            ? 'Available'
            : '';

          return (
            <button
              key={i}
              onClick={() => clickable && cell.slot && setSelectedSlotId(cell.slot.id === selectedSlotId ? null : cell.slot.id)}
              disabled={!clickable}
              title={title}
              style={style}
            >
              {formatTime(cell.start)}
              {cell.isBookedByMe && <span style={{ marginLeft: 4 }}>&#10003;</span>}
            </button>
          );
        })}
      </div>

      {/* Double-booking tooltip message (D-18) */}
      {hasDoubleBooked && (
        <p style={{ fontSize: 11, color: '#F97066', fontFamily: 'Poppins, sans-serif', marginTop: 8 }}>
          You already have a slot at this time
        </p>
      )}

      {/* Book button (D-19) */}
      {selectedSlotId && (
        <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={handleBook}
            disabled={booking}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: 'none',
              cursor: booking ? 'not-allowed' : 'pointer',
              background: 'linear-gradient(135deg, #c9a84c, #F59E0B)',
              color: '#fff',
              fontSize: 14,
              fontWeight: 600,
              fontFamily: 'Poppins, sans-serif',
              opacity: booking ? 0.7 : 1,
            }}
          >
            {booking ? 'Booking...' : 'Book This Slot'}
          </button>
          <button
            onClick={() => { setSelectedSlotId(null); onCancel?.(); }}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 11,
              color: '#94A3B8',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {error && (
        <p style={{ fontSize: 11, color: '#F97066', fontFamily: 'Poppins, sans-serif', marginTop: 8 }}>
          {error}
        </p>
      )}
    </div>
  );
}
